import { Form, Formik } from 'formik'
import { useLucaSignUp } from '../hooks/useLucaSignUp'
import { FieldWithErrorMessage } from '../../../ui/FieldWithErrorMsg'
import * as Yup from 'yup'
import { useMutationCreateOrganization } from '../queries/organizationQueries'
import { sileo } from 'sileo'
import { useState } from 'react'
import { UserAuth } from '../../../../../context/AuthContext'
import { useSwiper } from 'swiper/react'
import { useNavigate } from 'react-router-dom'
import { BaseHTTP } from '../../../../../api/base.http'
import tituloLoginSII from '../../../../../assets/tituloLoginSII.png'
import { encryptWithSecret } from '../../../../../utils/encrypt.util'
import {
  getStoredBackendToken,
  REGISTRATION_STATUS
} from '../../../../../utils/authSession'
import { pushGtmEvent } from '../../../../../utils/gtm'

const step4ValidationSchema = Yup.object({
  rut: Yup.string().required('Ingresa tu rut'),
  siiPassword: Yup.string().required('Ingresa tu clave tributaria')
})

const getSiiCredentialErrorMessage = error => {
  const status = error?.response?.status ?? error?.status
  const errorCode = String(error?.code || '').toLowerCase()
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message
  const normalizedMessage = String(responseMessage || '').toLowerCase()

  if (
    [400, 401, 403, 422].includes(status) ||
    normalizedMessage.includes('unauthorized') ||
    normalizedMessage.includes('wrong sii credentials')
  ) {
    return 'El RUT o la clave tributaria del SII son incorrectos. Revisa los datos e inténtalo nuevamente.'
  }

  if (Array.isArray(responseMessage)) return responseMessage.join(', ')

  const isValidationUnavailable =
    status === 504 ||
    errorCode === 'err_network' ||
    errorCode === 'econnaborted' ||
    normalizedMessage.includes('network error') ||
    normalizedMessage.includes('timeout')

  if (isValidationUnavailable) {
    return 'No pudimos validar tus credenciales del SII. Revisa el RUT y la clave tributaria e inténtalo nuevamente.'
  }

  return 'No pudimos validar tus credenciales del SII. Revisa los datos e inténtalo nuevamente.'
}

/**
 * Componente que permite completar la creación de la organización.
 * @returns {JSX.Element} El componente renderizado.
 */
export function StepForm4() {
  const { token, user, setBackendSession } = UserAuth()
  const setAccountProfile = useLucaSignUp(state => state.setAccountProfile)
  const accountProfile = useLucaSignUp(state => state.accountProfile)
  const organizationMutation = useMutationCreateOrganization()
  const [isLoading, setIsLoading] = useState(false)
  const [siiCredentialError, setSiiCredentialError] = useState('')
  const swiper = useSwiper()
  const navigate = useNavigate()

  const finalToken = token || getStoredBackendToken()

  const step4Values = {
    rut: accountProfile?.rut || '',
    siiPassword: accountProfile?.siiPassword || ''
  }

  /**
   * Maneja el envío del formulario final.
   * @param {object} values Valores del formulario.
   * @param {import('formik').FormikHelpers<object>} actions Funciones de Formik.
   */
  const handleFinalStepForm = async (values, actions) => {
    setAccountProfile(values)
    const ACCOUNT_PROFILE_PAYLOAD = {
      ...accountProfile,
      ...values
    }
    pushGtmEvent({
      event: 'organization_creation_attempt',
      user_type: ACCOUNT_PROFILE_PAYLOAD.userType
    })

    try {
      setIsLoading(true)
      setSiiCredentialError('')
      await createOrganizationAccount(ACCOUNT_PROFILE_PAYLOAD)

      pushGtmEvent({
        event: 'organization_created',
        user_type: ACCOUNT_PROFILE_PAYLOAD.userType  // 'contador' o 'empresa'
      })

      sileo.success({
        title: '¡Lista!',
        description: (
          <span className='text-green-500/80 font-medium'>Organización creada exitosamente 👌</span>
        )
      })

      actions.setSubmitting(true)
      localStorage.setItem('showWelcomePopup', 'true')
      navigate('/auth/signup?step=verification', { replace: true })
      swiper.slideNext()
    } catch (error) {
      const errorMessage = getSiiCredentialErrorMessage(error)
      setSiiCredentialError(errorMessage)
      actions.setSubmitting(false)
      sileo.error({
        title: 'Credenciales SII inválidas',
        description: (
          <span className='text-red-500/80 font-medium'>{errorMessage}</span>
        )
      })
    } finally {
      setIsLoading(false)
    }
  }

  const createOrganizationAccount = async values => {
    if (!finalToken) {
      throw new Error('No se encontró una sesión válida. Inicia sesión nuevamente.')
    }

    const encryptSecret = typeof window !== 'undefined' ? window.__APP_CONFIG__?.encryptSecret : ''
    const [encryptedRut, encryptedSiiPassword] = await Promise.all([
      encryptWithSecret(values.rut, encryptSecret),
      encryptWithSecret(values.siiPassword, encryptSecret)
    ])
    const organizationPayload = {
      name: values.companyName,
      userType: values.userType,
      siiCredentials: { user: encryptedRut, password: encryptedSiiPassword }
    }
    BaseHTTP.getInstance().setBackendToken(finalToken)
    const organizationData = await organizationMutation.mutateAsync(organizationPayload)
    const nextRegistrationStatus = user?.emailVerified
      ? REGISTRATION_STATUS.COMPLETED
      : REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING

    setBackendSession(
      { ...organizationData, registrationStatus: nextRegistrationStatus },
      nextRegistrationStatus,
      user
    )
  }

  return (
    <Formik
      validationSchema={step4ValidationSchema}
      initialValues={step4Values}
      onSubmit={handleFinalStepForm}
    >
      {({ isSubmitting }) => (
        <Form className="px-4 sm:px-0 w-full flex flex-col items-center">

          {/* Texto inicial */}
          <p className="font-sans text-center text-white/70 lg:text-gray-500 mb-3 max-w-md">
            ⚠️ Usa el RUT de la empresa, no tu RUT personal. La clave es la tributaria del SII (no ClaveÚnica).
          </p>

          {/* Imagen / título SII */}
          <div className="flex justify-center bg-primary h-[65px] w-full max-w-md py-2 rounded-md">
            <img src={tituloLoginSII} alt="tituloLoginSII" />
          </div>

          {/* Inputs */}
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md py-4">
            <li className="sm:col-span-2">
              <FieldWithErrorMessage
                name="rut"
                type="text"
                placeholder="Ej 12345678-9"
                label="Rut"
              />
            </li>

            <li className="sm:col-span-2">
              <FieldWithErrorMessage
                name="siiPassword"
                type="password"
                placeholder="Ingresa clave"
                label="Ingresa clave tributaria"
              />
            </li>
              </ul>

              {siiCredentialError && (
                <p className='mb-4 max-w-md text-center text-sm font-medium text-red-500'>
                  {siiCredentialError}
                </p>
              )}

              {/* Botones */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md">
            <button
              disabled={isSubmitting}
              onClick={() => swiper.slidePrev()}
              type="button"
              className="w-full py-4 rounded-xl btn-light-alt text-primary font-semibold text-sm shadow-md"
            >
              Volver
            </button>

            <button
              disabled={isSubmitting}
              type="submit"
              className="w-full py-4 rounded-xl bg-primary text-white font-semibold text-sm shadow-md"
            >
              {isLoading ? "Creando..." : "Continuar"}
            </button>
          </div>

          <p className='font-sans text-xs text-white/70 lg:text-gray-500 text-center mt-4 leading-relaxed'>
            🔒 Tus credenciales están protegidas y se usan solo para sincronizar tu empresa.
          </p>

          {/* Details */}
          <details className="text-sm text-white/80 sm:text-gray-700 mt-4 max-w-md w-full cursor-pointer">
            <summary className="text-white sm:text-gray-700 hover:text-white/80 sm:hover:text-gray-500">
              ¿Por qué pedimos tus credenciales del SII?
            </summary>

            <p className="mt-2 leading-relaxed text-white/70 sm:text-gray-600">
              Usamos tus credenciales para conectarnos al SII y obtener tu información tributaria automáticamente.
              Solo leemos los datos, no realizamos ninguna acción en tu nombre.
              Tus credenciales se almacenan de forma segura y encriptada.
            </p>
          </details>

        </Form>
      )}
    </Formik>
  )
}
